import type {
	AnchorHTMLAttributes,
	ButtonHTMLAttributes,
	DetailedHTMLProps,
	FC,
	ReactNode,
} from "react";
import type {
	ButtonProps as AriaButtonProps,
	LinkProps as AriaLinkProps,
} from "react-aria-components";

import { HelpCircle } from "@untitledui/icons";
import { forwardRef, isValidElement, useEffect, useMemo, useRef } from "react";
import { Button as AriaButton, Link as AriaLink } from "react-aria-components";

import { mergeRefs } from "@/components/command-menus/utils";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip/tooltip";
import { cx } from "@/utils/cx";
import { isReactComponent } from "@/utils/is-react-component";

import { styles } from "./button-styles";

/**
 * Props for the button variant (non-link)
 */
export interface ButtonProps
	extends
		CommonProps,
		DetailedHTMLProps<
			Omit<ButtonHTMLAttributes<HTMLButtonElement>, "color" | "slot">,
			HTMLButtonElement
		> {
	/** When true, the button will be excluded from the tab order */
	excludeFromTabOrder?: boolean;
	/** Slot name for react-aria component */
	slot?: AriaButtonProps["slot"];
}

/**
 * Common props shared between button and anchor variants
 */
export interface CommonProps {
	/** The color variant of the button */
	color?: keyof typeof styles.colors;
	/** Enables a tooltip to display next to the button */
	enableTooltip?: boolean;
	/** Icon component or element to show before the text */
	iconLeading?: FC<{ className?: string }> | ReactNode;
	/** Icon component or element to show after the text */
	iconTrailing?: FC<{ className?: string }> | ReactNode;
	/** Disables the button and shows a disabled state */
	isDisabled?: boolean;
	/** Shows a loading spinner and disables the button */
	isLoading?: boolean;
	/** When true, removes all borders, outlines, and box shadows */
	noBorders?: boolean;
	/** Removes horizontal padding from the text content */
	noTextPadding?: boolean;
	/** When true, keeps the text visible during loading state */
	showTextWhileLoading?: boolean;
	/** The size variant of the button */
	size?: keyof typeof styles.sizes;
	/** The description of the tooltip to display */
	tooltipDescription?: string;
	/** The title of the tooltip to display */
	tooltipTitle?: string;
}

/** Union type of button and link props */
export type Props = ButtonProps | LinkProps;

interface ButtonContentProps {
	children?: ReactNode;
	iconLeading?: FC<{ className?: string }> | ReactNode;
	iconTrailing?: FC<{ className?: string }> | ReactNode;
	loading?: boolean;
	noTextPadding?: boolean;
	showTextWhileLoading?: boolean;
}

/**
 * Props for the link variant (anchor tag)
 */
interface LinkProps
	extends
		CommonProps,
		DetailedHTMLProps<
			Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "color">,
			HTMLAnchorElement
		> {
	/** When true, the button will be excluded from the tab order */
	excludeFromTabOrder?: boolean;
	/**
	 * Options forwarded to the app router when this link is activated via
	 * react-aria `RouterProvider` (e.g. react-router `state`).
	 */
	routerOptions?: AriaLinkProps["routerOptions"];
}

const ButtonContent = ({
	children,
	iconLeading: IconLeading,
	iconTrailing: IconTrailing,
	loading,
	noTextPadding,
	showTextWhileLoading,
}: ButtonContentProps) => (
	<>
		{/* Leading icon */}
		{isValidElement(IconLeading) && IconLeading}
		{isReactComponent(IconLeading) && (
			<IconLeading className={styles.common.icon} data-icon="leading" />
		)}

		{loading && (
			<svg
				className={cx(
					styles.common.icon,
					!showTextWhileLoading &&
						"absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
				)}
				data-icon="loading"
				fill="none"
				viewBox="0 0 20 20"
			>
				{/* Background circle */}
				<circle
					className="stroke-current opacity-30"
					cx="10"
					cy="10"
					fill="none"
					r="8"
					strokeWidth="2"
				/>
				{/* Spinning circle */}
				<circle
					className="origin-center animate-spin stroke-current"
					cx="10"
					cy="10"
					fill="none"
					r="8"
					strokeDasharray="12.5 50"
					strokeLinecap="round"
					strokeWidth="2"
				/>
			</svg>
		)}

		{children && (
			<span
				className={cx("transition-inherit-all", !noTextPadding && "px-0.5")}
				data-text
			>
				{children}
			</span>
		)}

		{/* Trailing icon */}
		{isValidElement(IconTrailing) && IconTrailing}
		{isReactComponent(IconTrailing) && (
			<IconTrailing className={styles.common.icon} data-icon="trailing" />
		)}
	</>
);

export const Button = forwardRef<HTMLAnchorElement | HTMLButtonElement, Props>(
	(
		{
			children,
			className,
			color = "primary",
			enableTooltip = false,
			excludeFromTabOrder,
			iconLeading: IconLeading,
			iconTrailing: IconTrailing,
			isDisabled: disabled,
			isLoading: loading,
			noBorders,
			noTextPadding,
			showTextWhileLoading,
			size = "sm",
			tooltipDescription = "",
			tooltipTitle = "",
			...otherProps
		},
		forwardedRef,
	) => {
		const href = "href" in otherProps ? otherProps.href : undefined;
		const Component = href ? AriaLink : AriaButton;

		const isIcon = Boolean((IconLeading || IconTrailing) && !children);
		const isLinkType = ["link-color", "link-destructive", "link-gray"].includes(
			color,
		);

		noTextPadding = isLinkType || noTextPadding;

		// Extract tabIndex from otherProps to avoid conflicts when excludeFromTabOrder is set
		const { tabIndex: originalTabIndex, ...restProps } = otherProps;

		let props: object;

		if (href) {
			props = {
				...restProps,

				href: disabled ? undefined : href,

				// Since anchor elements do not support the `disabled` attribute and state,
				// we need to specify `data-rac` and `data-disabled` in order to be able
				// to use the `disabled:` selector in classes.
				...(disabled ? { "data-disabled": true, "data-rac": true } : {}),
			};
		} else {
			props = {
				...restProps,

				isDisabled: disabled,
				isPending: loading,
				type: restProps.type || "button",
			};
		}

		// Determine the final tabIndex value — always concrete so React Aria never
		// leaves a stale -1 on the DOM node when excludeFromTabOrder toggles to false.
		const finalTabIndex = excludeFromTabOrder ? -1 : (originalTabIndex ?? 0);

		// Ref to set tabIndex directly on DOM element (React Aria Components may override prop)
		const elementRef = useRef<HTMLElement | null>(null);

		// Force tabIndex on the DOM node because React Aria can re-apply its own
		// tabIndex after React's render pass. RAF re-applies when excluded; a single
		// sync when included clears any stale -1 left from a prior excluded render.
		useEffect(() => {
			const apply = () => {
				if (elementRef.current) {
					elementRef.current.tabIndex = finalTabIndex;
				}
			};
			apply();
			if (finalTabIndex !== -1) return;
			const id = requestAnimationFrame(apply);
			return () => cancelAnimationFrame(id);
		}, [finalTabIndex]);

		// Callback ref that works for both button and anchor elements
		const handleRef = useMemo(
			() => mergeRefs([elementRef, forwardedRef]),
			[forwardedRef],
		);

		const content = (
			<Component
				data-icon-only={isIcon ? true : undefined}
				data-loading={loading ? true : undefined}
				ref={handleRef}
				slot={excludeFromTabOrder ? "presentation" : undefined}
				{...props}
				{...(finalTabIndex !== undefined && { tabIndex: finalTabIndex })}
				className={cx(
					styles.common.root,
					styles.sizes[size].root,
					styles.colors[color].root,
					isLinkType && styles.sizes[size].linkRoot,
					// Link variants must stay shadow-free even inside TextField
					// (`data-input-wrapper`), where common styles add shadow-xs for
					// InputGroup addon buttons.
					isLinkType && "shadow-none in-data-input-wrapper:!shadow-none",
					(loading || (href && (disabled || loading))) && "pointer-events-none",
					// If in `loading` state, hide everything except the loading icon (and text if `showTextWhileLoading` is true).
					loading &&
						(showTextWhileLoading
							? "[&>*:not([data-icon=loading]):not([data-text])]:hidden"
							: "[&>*:not([data-icon=loading])]:invisible"),
					// Remove all borders, outlines, and box shadows when noBorders is true
					noBorders &&
						"!border-none !shadow-none !ring-0 !outline-hidden before:!border-none focus-visible:!outline-hidden",
					className,
				)}
			>
				<ButtonContent
					iconLeading={IconLeading}
					iconTrailing={IconTrailing}
					loading={loading}
					noTextPadding={noTextPadding}
					showTextWhileLoading={showTextWhileLoading}
				>
					{children}
				</ButtonContent>
			</Component>
		);

		if (enableTooltip) {
			return (
				<span className="inline-flex items-start gap-2">
					{content}
					<Tooltip description={tooltipDescription} title={tooltipTitle}>
						<TooltipTrigger className="group relative flex cursor-pointer flex-col items-center gap-2 text-fg-quaternary transition duration-100 ease-linear hover:text-fg-quaternary_hover focus:text-fg-quaternary_hover">
							<HelpCircle className="size-4" />
						</TooltipTrigger>
					</Tooltip>
				</span>
			);
		}

		return content;
	},
);

Button.displayName = "Button";
